import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { useToast } from "./use-toast";

export interface Pet {
  id: string;
  owner_id: string;
  name: string;
  type: string;
  breed?: string;
  color?: string;
  birth_date?: string;
  unit_number?: string;
  visible_to_residents: boolean;
  sex?: string;
  size?: string;
  weight?: number;
  observations?: string;
  photo_url?: string;
  vaccination_card_url?: string;
  condominium_id?: string;
  created_at: string;
  updated_at: string;
  owner?: {
    full_name: string;
  };
}

export const usePets = () => {
  const { profile } = useAuth();
  const { toast } = useToast();
  
  // Check if QueryClient is available
  let queryClient
  try {
    queryClient = useQueryClient()
  } catch {
    // Return mock data when QueryClient is not available
    return {
      pets: [],
      isLoading: false,
      createPet: () => {},
      updatePet: () => {},
      deletePet: () => {},
      uploadPhoto: async () => '',
      isCreating: false,
      isUpdating: false,
      isDeleting: false
    }
  }

  const { data: pets, isLoading } = useQuery({
    queryKey: ["pets", profile?.id],
    queryFn: async () => {
      if (!profile?.id) return [];

      let query = supabase
        .from("pets")
        .select("*")
        .order("created_at", { ascending: false });

      // Se for síndico, ver todos os pets do condomínio
      if (profile.role === "sindico" || profile.role === "administracao") {
        query = query.eq("condominium_id", profile.condominium_id);
      } else {
        // Moradores veem seus pets + pets visíveis do condomínio
        query = query.or(
          `owner_id.eq.${profile.id},and(visible_to_residents.eq.true,condominium_id.eq.${profile.condominium_id})`
        );
      }

      const { data: petsData, error } = await query;

      if (error) {
        console.error("Erro ao buscar pets:", error);
        toast({
          title: "Erro",
          description: "Erro ao carregar pets",
          variant: "destructive",
        });
        throw error;
      }

      if (!petsData || petsData.length === 0) {
        return [];
      }

      // Buscar informações dos donos
      const ownerIds = [...new Set(petsData.map(pet => pet.owner_id))];
      const { data: ownersData, error: ownersError } = await supabase
        .from('profiles')
        .select('id, full_name')
        .in('id', ownerIds);

      if (ownersError) {
        console.error("Erro ao buscar donos:", ownersError);
        // Continuar sem informações dos donos se der erro
        return petsData as Pet[];
      }

      // Mapear pets com informações dos donos
      const petsWithOwners = petsData.map(pet => ({
        ...pet,
        owner: ownersData?.find(owner => owner.id === pet.owner_id)
      })) as Pet[];

      return petsWithOwners;
    },
    enabled: !!profile?.id,
  });

  const createPetMutation = useMutation({
    mutationFn: async (petData: Omit<Pet, "id" | "created_at" | "updated_at">) => {
      const { data, error } = await supabase
        .from("pets")
        .insert([petData])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pets"] });
      toast({
        title: "Sucesso",
        description: "Pet cadastrado com sucesso!",
      });
    },
    onError: (error) => {
      console.error("Erro ao criar pet:", error);
      toast({
        title: "Erro",
        description: "Erro ao cadastrar pet",
        variant: "destructive",
      });
    },
  });

  const updatePetMutation = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<Pet> & { id: string }) => {
      const { data, error } = await supabase
        .from("pets")
        .update(updates)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pets"] });
      toast({
        title: "Sucesso",
        description: "Pet atualizado com sucesso!",
      });
    },
    onError: (error) => {
      console.error("Erro ao atualizar pet:", error);
      toast({
        title: "Erro",
        description: "Erro ao atualizar pet",
        variant: "destructive",
      });
    },
  });

  const deletePetMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("pets")
        .delete()
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pets"] });
      toast({
        title: "Sucesso",
        description: "Pet removido com sucesso!",
      });
    },
    onError: (error) => {
      console.error("Erro ao deletar pet:", error);
      toast({
        title: "Erro",
        description: "Erro ao remover pet",
        variant: "destructive",
      });
    },
  });

  const uploadPhoto = async (file: File, petId: string, type: 'photo' | 'vaccination_card') => {
    try {
      console.log('Iniciando upload:', { file: file.name, petId, type });
      
      const fileExt = file.name.split('.').pop();
      const fileName = `${profile?.id}/${petId}/${type}.${fileExt}`;
      
      console.log('Nome do arquivo:', fileName);
      
      const { error: uploadError, data: uploadData } = await supabase.storage
        .from('pet-photos')
        .upload(fileName, file, { upsert: true });

      if (uploadError) {
        console.error('Erro no upload:', uploadError);
        throw uploadError;
      }

      console.log('Upload realizado com sucesso:', uploadData);

      const { data: { publicUrl } } = supabase.storage
        .from('pet-photos')
        .getPublicUrl(fileName);

      console.log('URL pública gerada:', publicUrl);
      
      return publicUrl;
    } catch (error) {
      console.error('Erro ao fazer upload:', error);
      toast({
        title: "Erro",
        description: "Erro ao fazer upload da foto",
        variant: "destructive",
      });
      throw error;
    }
  };

  return {
    pets: pets || [],
    isLoading,
    createPet: createPetMutation.mutate,
    updatePet: updatePetMutation.mutate,
    deletePet: deletePetMutation.mutate,
    uploadPhoto,
    isCreating: createPetMutation.isPending,
    isUpdating: updatePetMutation.isPending,
    isDeleting: deletePetMutation.isPending,
  };
};